import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { useMutation } from "@apollo/client";
import { OrderStage } from "../../../types";
import { CANCEL_ORDER_MUTATION, COMMERCE_AUDIT_LOG_QUERY } from "../queries";
import { OrderType } from "../types";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { showSnackbar } from "@/lib/snackbarUtils";
import { NotePriorityValues } from "@/types";
import type { NotePriority } from "@/types";
import NoteInputFields from "@/features/global/components/NotesTable/NoteInputFields";
import { buildNoteInput } from "@/features/global/components/NotesTable/noteHelpers";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";

interface CancelOrderFormValues {
  noteContent: string;
  notePriority: NotePriority;
}

export const CancelOrderModal = ({
  onDismiss,
  order,
  refetch,
  returnTo
}: {
  onDismiss: () => void;
  order: OrderType;
  refetch?: () => void;
  returnTo?: () => void;
}) => {
  const { trackEvent } = useAmplitude();
  const [cancelOrderMutation, { loading }] = useMutation(CANCEL_ORDER_MUTATION, {
    refetchQueries: [COMMERCE_AUDIT_LOG_QUERY]
  });
  const headline = order.status === OrderStage.DRAFT ? "Cancel Draft Order" : "Cancel Order";
  const { control, getValues } = useForm<CancelOrderFormValues>({
    defaultValues: { noteContent: "", notePriority: NotePriorityValues.NONE }
  });

  let content =
    order.status === OrderStage.DRAFT
      ? "Are you sure you want to cancel this order?"
      : "Are you sure you want to cancel this order? The payor will no longer be able to pay this order. This cannot be undone.";
  const [errorContent, setErrorContent] = useState<string>("");

  async function cancelCommerceOrder() {
    const noteContent = getValues("noteContent");
    const notePriority = getValues("notePriority");
    const { data } = await cancelOrderMutation({
      variables: {
        input: {
          commerceOrderId: order.id,
          note: buildNoteInput(noteContent, notePriority, headline)
        }
      }
    });

    if (data.cancelCommerceOrder.success) {
      refetch && refetch();
      returnTo && returnTo();
      showSnackbar("Order canceled");
      onDismiss();
      trackEvent(
        BILLING_ANALYTICS_EVENTS.orderCanceled,
        getBillingAnalyticsData({
          source: "orders",
          orderId: order.id,
          action: order.status === OrderStage.DRAFT ? "cancel_draft" : "cancel"
        })
      );
    } else {
      setErrorContent(data.cancelCommerceOrder.message);
    }
  }

  const actions = (
    <>
      <Button variant="text" onClick={onDismiss}>
        Go Back
      </Button>

      <Button onClick={cancelCommerceOrder} disabled={loading}>
        Cancel Order
      </Button>
    </>
  );

  return (
    <Dialog onClosed={onDismiss} open actions={actions} headline={headline} displayOverflow hasFormElements>
      {errorContent ? <div className="text-red-500 font-bold text-center mb-4">{errorContent}</div> : null}
      {content}
      <div className="mt-6">
        <NoteInputFields control={control} contentName="noteContent" priorityName="notePriority" />
      </div>
    </Dialog>
  );
};
